// What a game tells its parent page about its own loading, per studio.
// Same table as apps/web's services/gameLoadProtocols.ts — keep the two in sync.
//
// The game runs in a cross-origin iframe, so its network and DOM are invisible
// to us — the only runtime signals are the iframe's `load` event and whatever
// the game chooses to postMessage. Each protocol below was found by recording
// real games from our catalogue (Oct 2026) and checking the screenshots: the
// ready message lands on the frame where the game becomes playable.
//
// Games must be recognised *before* their first message (which can come 10s+
// after `load`, while the game is still downloading), hence matching on launch
// host + gameId. Anything unmatched sent nothing useful when recorded —
// GoldenPhoenix, Juwa, rgs, algNET, GambleHubLive, Vegas' non-classic engines
// (Amanet only posts if the provider enables it), PlayXSpin's PG Soft /
// Habanero, Slotserv's 421/810/930/939/960 studios, GambleHub's other studios —
// and gets a network-scaled estimate instead (silentHoldFor, at the bottom).
export interface LoadProtocol {
  /** 0–100 if this message reports loading progress. */
  progress: (data: any) => number | null;
  /** The game has finished loading and is playable. */
  isReady: (data: any) => boolean;
  /** The game opened its own dialog (e.g. an error) — the player must see it. */
  isBlocking: (data: any) => boolean;
  /**
   * Longest silence measured between the iframe's `load` and the game's first
   * message (fast connection, with margin). Past it, the page isn't the game
   * talking — usually the provider's error page — so the splash steps aside.
   */
  firstMessageMs: number;
}

const never = () => false;
const noProgress = () => null;

// Pragmatic counts loadingProgress up to 99, then sends gameLoadingEnded.
// Careful: its *start* message is {name: 'gameLoadingStarted', event: 'loaded'}.
const PRAGMATIC: LoadProtocol = {
  progress: (d) => (d?.event === 'loadingProgress' ? Number(d.params?.percentage) || 0 : null),
  isReady: (d) => d?.name === 'gameLoadingEnded',
  isBlocking: (d) => d?.event === 'systemMessageShown',
  firstMessageMs: 6000, // 2.7s seen
};

// Slotserv's 3xx studio: 'preloader/show', then {localizedText, percentage: 0–1},
// then 'splashScreen/ready' as the START button appears.
const SLOTSERV_PRELOADER: LoadProtocol = {
  progress: (d) =>
    d && typeof d === 'object' && 'localizedText' in d && typeof d.percentage === 'number'
      ? d.percentage * 100
      : null,
  isReady: (d) => d === 'splashScreen/ready',
  isBlocking: never,
  firstMessageMs: 12000, // 6–8s seen
};

// Betsoft (Slotserv Dedicated): exi_onGameDataLoaded, then exi_onGameInitialized
// as the reels appear.
const BETSOFT: LoadProtocol = {
  progress: noProgress,
  isReady: (d) => d?.exi_fMessageType_str === 'exi_onGameInitialized',
  isBlocking: never,
  firstMessageMs: 5000, // ~2s seen
};

// Light & Wonder / SG Digital (GambleHub "magic-slots:sg"), posted from a
// nested frame: onGcmInit, gameRevealed, then gameAnimationComplete. Wait for
// the last one — right after gameRevealed the game still shows "PLEASE WAIT".
const SG_DIGITAL: LoadProtocol = {
  progress: noProgress,
  isReady: (d) => d?.gcmevent === 'gameAnimationComplete',
  isBlocking: never,
  firstMessageMs: 4000, // <1s seen
};

// Habanero (GambleHub "magic-slots:habanero"), JSON strings: gameLoadingProgress
// 0–100, gameLoadingEnded, then gameReady as the reels appear.
const HABANERO: LoadProtocol = {
  progress: (d) => (d?.type === 'gameLoadingProgress' ? Number(d.progress) || 0 : null),
  isReady: (d) => d?.type === 'gameReady',
  isBlocking: never,
  firstMessageMs: 4000, // immediate
};

// 3 Oaks (GambleHub "black:3oaks"): game_progress 0–100, then game_ready.
const THREE_OAKS: LoadProtocol = {
  progress: (d) => (d?.event === 'game_progress' ? Number(d.data?.progress) || 0 : null),
  isReady: (d) => d?.event === 'game_ready',
  isBlocking: never,
  firstMessageMs: 4000, // ~1s seen
};

// Greentube (GambleHub "greece:400"): JSON-string events plus plain strings;
// loadingProgress 0–100, then "GAME_MODE:READY" as its splash closes.
const GREENTUBE: LoadProtocol = {
  progress: (d) => (d?.event?.name === 'loadingProgress' ? Number(d.event.value) || 0 : null),
  isReady: (d) => d === 'GAME_MODE:READY',
  isBlocking: never,
  firstMessageMs: 4000, // <1s seen
};

// Wazdan (GambleHub "black:wazdan"), from a nested frame: WGEAPI.status.ready.
const WAZDAN: LoadProtocol = {
  progress: noProgress,
  isReady: (d) => d?.method === 'WGEAPI.status.ready',
  isBlocking: never,
  firstMessageMs: 4000, // ~1.2s seen
};

// Vegas' classic engine (launch path /launcherIframe/<Name>/ with no engine
// suffix, e.g. "Africa") removes its preloader, then posts this "focus".
// Its other engines (…AM, …EGT, …VP, …WD …) send nothing.
const VEGAS_CLASSIC: LoadProtocol = {
  progress: noProgress,
  isReady: (d) => d?.func === 'parentFunc' && d.message === 'focus',
  isBlocking: never,
  firstMessageMs: 10000, // its only message is the ready one: 1.5–6.4s seen
};

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch (_) {
    return null;
  }
};

const onDomain = (host: string, domain: string) => host === domain || host.endsWith('.' + domain);

export const loadProtocolFor = (launchUrl: string, gameId: string): LoadProtocol | null => {
  const url = parseUrl(launchUrl);
  const host = url?.hostname ?? '';
  const path = url?.pathname ?? '';
  // PlayXSpin serves several studios; "1:" is its Pragmatic prefix.
  if (onDomain(host, 'wwg-online.com') && gameId.startsWith('1:')) return PRAGMATIC;
  if (gameId.startsWith('magic-slots:pragmatic:')) return PRAGMATIC;
  if (gameId.startsWith('magic-slots:habanero:')) return HABANERO;
  if (gameId.startsWith('black:3oaks:')) return THREE_OAKS;
  if (gameId.startsWith('greece:400:')) return GREENTUBE;
  if (gameId.startsWith('black:wazdan:')) return WAZDAN;
  // Slotserv Default: studio prefixes 320–336 all speak this; 421/810/9xx don't.
  if (onDomain(host, 'slotserv.com') && /^3[23]\d:/.test(gameId)) return SLOTSERV_PRELOADER;
  if (onDomain(host, 'xtrtnews.com')) return BETSOFT;
  if (gameId.startsWith('magic-slots:sg:')) return SG_DIGITAL;
  if (onDomain(host, 'gamesapi.live') && /^\/launcherIframe\/[A-Za-z0-9]*[a-z0-9]\//.test(path)) return VEGAS_CLASSIC;
  return null;
};

// Silent games: how long their own loader keeps running after their page has
// loaded, measured per provider/studio from recordings on a fast connection
// (extraMs), and how long that page took to load there (refLoadMs). At runtime
// the extra is scaled by how much slower this player's first page load was
// than refLoadMs — the network check — and restarted on every further `load`,
// so redirect chains (GoldenPhoenix: 3 loads) are covered without guessing.
export interface SilentHold {
  refLoadMs: number;
  extraMs: number;
}

const DEFAULT_SILENT_HOLD: SilentHold = { refLoadMs: 1500, extraMs: 6000 };

// GambleHub studios that stay silent; extra = playable − page load, recorded.
const GAMBLEHUB_SILENT: Array<[prefix: string, extraMs: number]> = [
  ['magic-slots:rplslots:', 10000], // Amanet engine: 7–14s
  ['magic-slots:aristocrat:', 9000], // 8–11s
  ['magic-slots:wms:', 6500],
  ['magic-slots:firekirin:', 7000], // 5.4–7.7s
  ['magic-slots:egaming:', 4500],
  ['magic-slots:vibragaming:', 5000],
  ['black:apollo:', 6000],
  ['greece:3300:', 5500],
  ['nova:', 4500],
];

// GoldenPhoenix varies far too much per game for one value (3.7–19s after its
// last page load), so each game is measured: gameId → extraMs (+1s margin).
// Recorded Oct 2026 by watching each game switch from its loader to the game.
// Games not listed (new ones, Bingo Party, Fortune Roulette) get 9s.
// The same game varied up to ~1.8× between runs (their CDN, not the page load
// our network check sees), so the hold doubles the measurement: a fast load
// waits a little behind our logo rather than showing their loader.
const GOLDEN_PHOENIX_SAFETY = 2;
const GOLDEN_PHOENIX_EXTRA_MS: Record<string, number> = {
  2018: 11500, 2019: 17000, 2030: 12500, 2038: 12000, 2040: 12000, 2042: 14500,
  2062: 20500, 2705: 5500, 2706: 6000, 2707: 7500, 2708: 14500, 2709: 5500,
  2710: 5500, 2711: 17000, 2712: 10000, 2713: 6500, 2714: 10000, 2715: 7000,
  2716: 7000, 2717: 6500, 2718: 5500, 2719: 6500, 2720: 9000, 2721: 6000,
  2722: 6000, 2723: 8500, 2724: 5500, 2725: 10500, 2726: 6000, 2728: 16500,
  2729: 5500, 2730: 7500, 2731: 16500, 2732: 5000, 2733: 6000, 2734: 6500,
  2735: 6500, 2736: 6500, 2737: 7500, 2738: 7500, 2739: 6000, 2740: 6000,
  2741: 6000, 2742: 9500, 2743: 8000, 2744: 8000, 2745: 6000, 2746: 6000,
  2747: 7000, 2748: 10000, 2749: 8500, 2750: 6500, 2751: 7000, 2796: 9000,
  2797: 6000, 2798: 11500, 2799: 7000, 2800: 8000, 2801: 7500, 2802: 8000,
  2803: 12000, 2804: 11000, 2805: 14000, 2806: 11000, 2807: 6000, 2808: 7500,
  2809: 7500, 2810: 8000, 2811: 8000, 2812: 8000, 2813: 9500, 2815: 8500,
  2816: 10000, 2817: 10500, 2818: 10500, 2819: 7500, 2820: 6500, 2821: 8500,
};

export const silentHoldFor = (launchUrl: string, gameId: string): SilentHold => {
  const url = parseUrl(launchUrl);
  const host = url?.hostname ?? '';
  // GoldenPhoenix: loads at ~0.6s, ~1.2s, ~3s, then its loader (per game, above).
  if (onDomain(host, 'gldp.cc')) {
    return { refLoadMs: 600, extraMs: (GOLDEN_PHOENIX_EXTRA_MS[gameId] ?? 9000) * GOLDEN_PHOENIX_SAFETY };
  }
  // Juwa: one load at ~2–3s, then ~28MB of assets (Fish Server: playable at 17s).
  if (/^212\.47\.64\.134$/.test(host)) return { refLoadMs: 2200, extraMs: 15000 };
  // rgs: its loader already shows our brand logo; 3.5–5.5s after load.
  if (onDomain(host, 'gamesapi.live') && !/^\/launcherIframe\//.test(url?.pathname ?? '')) {
    return { refLoadMs: 1500, extraMs: 4500 };
  }
  // Vegas' silent engines, by launch path suffix (/launcherIframe/<Name><ENGINE>/).
  const vegasEngine = /^\/launcherIframe\/[A-Za-z0-9]*?([A-Z]{2,4})\//.exec(url?.pathname ?? '')?.[1];
  if (onDomain(host, 'gamesapi.live') && vegasEngine) {
    if (vegasEngine === 'PG') return { refLoadMs: 2200, extraMs: 16000 }; // Super Wheel: CONTINUE at ~18s
    if (vegasEngine === 'AM') return { refLoadMs: 4500, extraMs: 23000 }; // Amanet: ~5%/s loader, ~27s
  }
  if (onDomain(host, 'slotserv.com')) {
    if (gameId.startsWith('930:')) return { refLoadMs: 1200, extraMs: 3500 }; // after its 3rd load
    if (gameId.startsWith('939:')) return { refLoadMs: 1300, extraMs: 5000 }; // PG Soft
  }
  const studio = GAMBLEHUB_SILENT.find(([prefix]) => gameId.startsWith(prefix));
  if (studio) return { refLoadMs: 1000, extraMs: studio[1] };
  return DEFAULT_SILENT_HOLD;
};
